/**
 * Visits panel Step B — the Activity tab of a grouped agent row: the
 * records behind the row (hourly visit buckets, ADR-0034), newest first,
 * from `GET /v1/sites/:site/visit-groups/:group_key/records` with the same
 * filters the row was counted under, so the list adds up to the row.
 * Clicking a record opens it in the detail panel. Mount with
 * `key={group_key + filters}` so a new group starts from page 1.
 */

import { useEffect, useState } from 'react';

import { humanRecordName, identityState, isHumanVisit } from '@sill/audit';
import type { AuditRecord, SiteId } from '@sill/types';
import { INBOUND_PROTOCOL_LABEL } from '@sill/types';
import type { AuditQuery, VisitGroup } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import type { SetView } from '../../app/view-router.js';
import { Pill, RelativeTime } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { PersonGlyph } from '../people/PersonGlyph.js';
import { effectiveOutcome } from './effective-outcome.js';
import { SecurityFlagChip } from './SecurityFlagChip.js';
import { VisitIdentityMark } from './VisitIdentityMarks.js';
import type { MandateRecord } from './types.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface VisitGroupActivityProps {
  readonly siteId: SiteId;
  readonly group: VisitGroup;
  /** The list's filters (minus `page`), so the records add up to the row. */
  readonly query: AuditQuery;
  readonly onOpenRecord: (record: MandateRecord) => void;
  /** Founder bug 2026-10-20 — chip opens Security › Attackers. */
  readonly setView?: SetView;
}

export function VisitGroupActivity({ siteId, group, query, onOpenRecord, setView }: VisitGroupActivityProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const [records, setRecords] = useState<MandateRecord[] | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    const list = api.mandates.listGroupRecords;
    if (list === undefined) { setFailed(true); return undefined; }
    let cancelled = false;
    setLoading(true);
    void list
      .call(api.mandates, siteId, group.group_key, { ...query, page })
      .then((res) => {
        if (cancelled) return;
        const rows = res.records as MandateRecord[];
        setRecords((prev) => (page === 1 || prev === null ? rows : [...prev, ...rows]));
        setTotalPages(res.total_pages ?? 1);
      })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, siteId, group.group_key, queryKey, page]);

  const kicker = `${group.records.toLocaleString('en-US')} RECORD${group.records === 1 ? '' : 'S'} · ${group.visits.toLocaleString('en-US')} VISIT${group.visits === 1 ? '' : 'S'}`;

  // ADR-0253 §A — "Protocols" line (per-group counts by derived inbound
  // protocol). The server attached `protocols: [{protocol, count}]` to
  // the group; older APIs omit it. We render in the vocabulary's natural
  // order ("MCP 3 · A2A 1 · Web (HTTP) 47,303") so sites with wide bot
  // traffic read the same way across groups.
  const PROTOCOL_ORDER: readonly string[] = ['web', 'mcp', 'a2a', 'ap2', 'ucp', 'acp', 'pap', 'unknown'];
  const protocols = group.protocols;
  const protocolsLine = protocols === undefined || protocols.length === 0
    ? null
    : [...protocols]
        .sort((a, b) => {
          const ia = PROTOCOL_ORDER.indexOf(a.protocol);
          const ib = PROTOCOL_ORDER.indexOf(b.protocol);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        })
        .map((p) => {
          const label = INBOUND_PROTOCOL_LABEL[p.protocol as keyof typeof INBOUND_PROTOCOL_LABEL]
            ?? p.protocol.toUpperCase();
          return `${label} ${p.count.toLocaleString('en-US')}`;
        })
        .join(' · ');

  return (
    <div data-testid="visit-group-activity">
      <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5, marginBottom: 10 }}>
        {kicker}
      </div>
      {protocolsLine !== null ? (
        <div
          data-testid="visit-group-protocols"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 0.5,
            marginBottom: 10,
          }}
        >
          Protocols · {protocolsLine}
        </div>
      ) : null}
      {failed && records === null ? (
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>Activity could not be loaded.</div>
      ) : records === null ? (
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>LOADING ACTIVITY…</div>
      ) : (
        <div style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 2 }}>
          {records.map((r, i) => (
            <ActivityRecordRow
              key={r.record_id}
              C={C}
              record={r}
              isLast={i === records.length - 1}
              onOpen={() => onOpenRecord(r)}
              {...(setView !== undefined ? { setView } : {})}
            />
          ))}
        </div>
      )}
      {records !== null && page < totalPages ? (
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={loading}
          style={{
            marginTop: 10,
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '5px 10px',
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 1.5,
            color: C.muted,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'LOADING…' : 'LOAD OLDER'}
        </button>
      ) : null}
    </div>
  );
}

function ActivityRecordRow({
  C,
  record: r,
  isLast,
  onOpen,
  setView,
}: {
  C: ReturnType<typeof useC>;
  record: MandateRecord;
  isLast: boolean;
  onOpen: () => void;
  setView?: SetView;
}): JSX.Element {
  const result = identityState(r as AuditRecord);
  const outcome = effectiveOutcome(r);
  const count = r.visit_count ?? 1;
  const sec = r.security_flag;
  return (
    <button
      type="button"
      data-activity-record-id={r.record_id}
      onClick={onOpen}
      className="flex items-center gap-3 w-full"
      style={{
        padding: '8px 10px',
        background: 'transparent',
        border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <RelativeTime
        iso={count > 1 && r.last_visit_at !== undefined ? r.last_visit_at : r.evaluated_at}
        style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, flexShrink: 0, minWidth: 56 }}
      />
      {outcome.label !== 'OBSERVED' ? <Pill tone={outcome.tone}>{outcome.label}</Pill> : null}
      {/* People rows: who each visit was ("Visitor from Austin"). */}
      {isHumanVisit(r) ? (
        <span className="inline-flex items-center gap-1.5" style={{ flexShrink: 0, maxWidth: '45%', minWidth: 0 }} data-testid="activity-person">
          <PersonGlyph country={r.geo_country} size={14} />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {humanRecordName(r)}
          </span>
        </span>
      ) : null}
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.text,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {r.page_path !== undefined && r.page_path.length > 0 ? r.page_path : 'visit'}
      </span>
      <VisitIdentityMark record={r} result={result} />
      {sec !== undefined ? (
        <SecurityFlagChip flag={sec} {...(setView !== undefined ? { setView } : {})} surface="row" />
      ) : null}
      {count > 1 ? (
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.skyFg, flexShrink: 0 }} aria-label={`${count} visits aggregated`}>
          ×{count.toLocaleString('en-US')}
        </span>
      ) : null}
    </button>
  );
}
